import React, { useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { getPremioWebUrl } from '../premio-urls';

const firstDefined = (...values) => values.find(value => value !== undefined && value !== null);
const text = value => typeof value === 'string' || typeof value === 'number' ? String(value) : '';
const asList = value => Array.isArray(value) ? value : [];
const normalized = value => text(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const conditional = value => !!(value?.visibleWhen || value?.visible_when || value?.conditional);
const activeApplication = value => value && value.activo !== false && value.active !== false && (value.scope ?? 'application') === 'application';

// Relative assets belong to the Premio site, which has a different origin from admin.
const assetUrl = value => {
  const source = text(value).trim();
  if (!source) return undefined;
  try {
    const candidate = /^\/?assets\//i.test(source)
      ? new URL(`/${source.replace(/^\//, '')}`, getPremioWebUrl())
      : /^https?:\/\//i.test(source) ? new URL(source) : null;
    return candidate && ['http:', 'https:'].includes(candidate.protocol) ? candidate.href : undefined;
  } catch (error) {
    return undefined;
  }
};

function PreviewImage({ value, label, className }) {
  const source = assetUrl(value);
  const [failedSource, setFailedSource] = useState(null);
  return source && failedSource !== source ? (
    <img src={source} alt={label} className={className} onError={() => setFailedSource(source)} />
  ) : (
    <div className={`premio-preview__image-placeholder ${className || ''}`}>
      <span>{label}</span>
      <small>{source ? 'No se pudo cargar la imagen. Revisa el archivo o su enlace.' : 'Selecciona una imagen para verla aquí.'}</small>
    </div>
  );
}

function LinkedText({ value, fallback, label }) {
  const content = text(value ?? fallback);
  const includesLabel = normalized(content).includes(normalized(label));
  return includesLabel ? <span className="premio-preview__link">{content}</span> : (
    <>{content}{content ? ' ' : ''}<span className="premio-preview__link">{label}</span></>
  );
}

function LoginPreview({ value }) {
  const branding = value.branding || {};
  const login = branding.login || {};
  return (
    <div className="premio-preview__login">
      <div className="premio-preview__hero">
        <PreviewImage value={branding.hero_image_url} label="Imagen de portada" />
      </div>
      <div className="premio-preview__login-content">
        <PreviewImage value={branding.login_logo_url || branding.logo_url} label="Logo del premio" className="premio-preview__logo" />
        <h2 className="premio-preview__heading">{login.title ?? branding.visible_title ?? value.title ?? 'Nombre del premio'}</h2>
        <div className="premio-preview__login-card">
          <h3>Iniciar sesión</h3>
          <p>{login.subtitle ?? 'Ingresa con tu cuenta de Google para continuar'}</p>
          <button type="button" className="premio-preview__google-button" disabled>
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62z" />
              <path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.34 0-4.33-1.58-5.04-3.71H.96v2.33A9 9 0 0 0 9 18z" />
              <path fill="#FBBC05" d="M3.96 10.71a5.41 5.41 0 0 1 0-3.42V4.96H.96a9 9 0 0 0 0 8.08l3-2.33z" />
              <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l3 2.33C4.67 5.16 6.66 3.58 9 3.58z" />
            </svg>
            Iniciar sesión con Google
          </button>
          <div className="premio-preview__divider"><span>Acceso rápido y seguro</span></div>
          <p><LinkedText value={login.terms_text} fallback="Al continuar, aceptas nuestros" label="Términos de Servicio y Política de Privacidad" /></p>
          <p><LinkedText value={login.bases_text} fallback="Aquí puedes ver las" label="Bases del Premio" /></p>
        </div>
        <p className="premio-preview__copyright">© 2025 Chile Converge. Todos los derechos reservados.</p>
      </div>
    </div>
  );
}

function Help({ value }) {
  if (Array.isArray(value)) return <ul className="premio-preview__help">{value.map((line, index) => <li key={index}>{text(line)}</li>)}</ul>;
  return value ? <p className="premio-preview__help">{text(value)}</p> : null;
}

function QuestionPreview({ field, id }) {
  const type = firstDefined(field.tipo, field.type, 'text');
  const isLong = ['textarea', 'long_text'].includes(type);
  const isMulti = ['multiselect', 'checkbox-group'].includes(type);
  const isConsent = ['consent', 'checkbox'].includes(type);
  const isFull = isLong || isMulti || isConsent || type === 'file' || field.key === 'region';
  const options = asList(firstDefined(field.opciones, field.options)).filter(option => option !== null && option !== undefined);
  const required = firstDefined(field.requerido, field.required, true);
  const limit = firstDefined(field.maxLength, field.max_length);
  const inputType = ['number', 'email', 'tel', 'date'].includes(type) ? type : 'text';
  const title = text(field.label) || 'Pregunta sin título';
  const optionLabel = option => typeof option === 'string' ? option : text(option.label ?? option.value);
  return (
    <div className={`premio-preview__field${isFull ? ' premio-preview__field--full' : ''}`}>
      <label className="premio-preview__label" htmlFor={!isMulti ? id : undefined} id={`${id}-label`}>
        {title}{required && <span className="premio-preview__required"> *</span>}
      </label>
      <Help value={firstDefined(field.subtitle, field.ayuda)} />
      {conditional(field) && <p className="premio-preview__help">Se muestra según las respuestas anteriores.</p>}
      {isLong ? (
        <>
          <textarea id={id} disabled placeholder={text(field.placeholder)} rows={Math.max(2, Math.min(Number(field.rows) || 3, 8))} />
          {limit && <span className="premio-preview__counter">0 / {text(limit)}</span>}
        </>
      ) : type === 'select' ? (
        <select id={id} disabled defaultValue="">
          <option value="">{field.placeholder || 'Selecciona una opción'}</option>
          {options.map((option, index) => <option key={index} value={index + 1}>{optionLabel(option)}</option>)}
        </select>
      ) : isMulti ? (
        <div className="premio-preview__options" role="group" aria-labelledby={`${id}-label`}>
          {options.map((option, index) => <label className="premio-preview__checkbox" key={index}><input type="checkbox" disabled />{optionLabel(option)}</label>)}
          {field.maxSelections && <p className="premio-preview__help">Puedes elegir hasta {text(field.maxSelections)} opciones.</p>}
        </div>
      ) : isConsent ? (
        <label className="premio-preview__checkbox"><input id={id} type="checkbox" disabled />{text(field.consentText) || title}</label>
      ) : type === 'file' ? (
        <>
          <input id={id} type="file" disabled accept={text(field.accept)} />
          <p className="premio-preview__help">Formatos aceptados: {text(field.accept) || '.pdf, .jpg, .jpeg, .png'} (máx. {text(field.maxSizeMb) || '5'} MB).</p>
        </>
      ) : <input id={id} type={inputType} disabled placeholder={text(field.placeholder)} />}
    </div>
  );
}

function FormPreview({ value }) {
  const branding = value.branding || {};
  const sections = asList(value.form_schema?.pasos).filter(activeApplication);
  return (
    <div className="premio-preview__application">
      <header className="premio-preview__application-header">
        <PreviewImage value={branding.logo_url || branding.login_logo_url} label="Logo del premio" className="premio-preview__logo" />
        <h2>{branding.visible_title || value.title || 'Nombre del premio'}</h2>
      </header>
      <div className="premio-preview__steps"><span>1</span><strong>Formulario de postulación</strong></div>
      <p className="premio-preview__note">Vista previa del Formulario 1. Las preguntas y opciones condicionales se muestran para revisar su contenido; en la postulación aparecen según las respuestas.</p>
      {!sections.length && <p className="premio-preview__empty">Agrega las secciones y preguntas del Formulario 1 para verlas aquí.</p>}
      {sections.map((section, sectionIndex) => (
        <section className="premio-preview__section" key={sectionIndex}>
          <h3>{firstDefined(section.title, section.titulo) || `Sección ${sectionIndex + 1}`}</h3>
          <Help value={firstDefined(section.description, section.descripcion)} />
          {conditional(section) && <p className="premio-preview__help">Esta sección se muestra según las respuestas anteriores.</p>}
          <div className="premio-preview__field-grid">
            {asList(firstDefined(section.campos, section.fields)).filter(activeApplication).map((field, fieldIndex) => (
              <QuestionPreview key={fieldIndex} field={field} id={`premio-preview-${sectionIndex}-${fieldIndex}`} />
            ))}
          </div>
        </section>
      ))}
      {!!sections.length && <p className="premio-preview__help">Los campos marcados con <span className="premio-preview__required">*</span> son obligatorios.</p>}
    </div>
  );
}

export default function PremioPreview({ open, onClose, value = {}, step = 0 }) {
  const branding = value.branding || {};
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth aria-labelledby="premio-preview-title" PaperProps={{ className: 'premio-preview' }}>
      <DialogTitle id="premio-preview-title" className="premio-preview__title">{step === 2 ? 'Vista previa del Formulario 1' : 'Vista previa del inicio de sesión'}</DialogTitle>
      <DialogContent className="premio-preview__dialog-content">
        <p className="premio-preview__note">Revisa tus cambios antes de guardar. Esta vista previa no permite iniciar sesión ni enviar postulaciones.</p>
        <div style={{ '--premio-primary': branding.primary_color || '#003965', '--premio-secondary': branding.secondary_color || '#E42313', fontFamily: branding.font_family || 'inherit' }}>
          {step === 2 ? <FormPreview value={value} /> : <LoginPreview value={value} />}
        </div>
      </DialogContent>
      <DialogActions className="premio-preview__actions"><button type="button" className="button secondary" onClick={onClose}>Cerrar vista previa</button></DialogActions>
    </Dialog>
  );
}
